import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUpdateCourse } from '../../queries/moodle/useUpdateCourse';

const toDateStr = (val) => {
  if (!val) return '';
  const d = typeof val === 'number' ? new Date(val * 1000) : new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toISOString().slice(0, 10);
};

const schema = z
  .object({
    recurrence: z.enum(['YEARLY', 'MONTHLY', 'ONCE']),
    launchDate: z.string().min(1, 'Launch date is required'),
    endDate: z.string().min(1, 'End date is required'),
    graceDays: z.coerce.number().min(0, 'Must be 0 or more'),
    freezeDays: z.coerce.number().int().min(0, 'Must be 0 or more'),
    isMandatory: z.boolean(),
    isActive: z.boolean(),
    warningNotificationDays: z.array(z.number()).default([]),
    completionCcEmails: z
      .array(z.string().email('Invalid email address'))
      .default([]),
    applicableLocationIds: z.array(z.number()).default([]),
  })
  .superRefine((data, ctx) => {
    if (data.warningNotificationDays.some((val) => val > data.graceDays)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Values must be <= ${data.graceDays}`,
        path: ['warningNotificationDays'],
      });
    }
  });

export const useUpdateCourseForm = (course, onSuccess) => {
  const form = useForm({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (course) {
      form.reset({
        recurrence: course.recurrence,
        launchDate: toDateStr(course.launchDate),
        endDate: toDateStr(course.endDate),
        graceDays: course.graceDays ?? course.graceDaysNewJoiner ?? 0,
        freezeDays: course.freezeDays ?? 0,
        isMandatory: course.isMandatory,
        isActive: course.isActive,
        warningNotificationDays: course.warningNotificationDays || [],
        completionCcEmails: course.completionCcEmails
          ? typeof course.completionCcEmails === 'string'
            ? course.completionCcEmails
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean)
            : course.completionCcEmails
          : [],
        applicableLocationIds: course.applicableLocationIds
          ? typeof course.applicableLocationIds === 'string'
            ? course.applicableLocationIds
                .split(',')
                .map((id) => Number(id.trim()))
                .filter((id) => !isNaN(id))
            : course.applicableLocationIds
          : [],
      });
    }
  }, [course]);

  const { mutate, isPending } = useUpdateCourse(onSuccess);

  const onSubmit = form.handleSubmit(({ graceDays, ...rest }) =>
    mutate({
      id: course.id,
      ...rest,
      graceDays,
    }),
  );

  return { form, onSubmit, isPending };
};
